import { funnelHref, LEGAL_LINE } from "@/lib/site";
import { trackConversion } from "@/lib/track";

const Step = ({ n, t, s }) => <article><span>{n}</span><h3>{t}</h3><p>{s}</p></article>;

export const Method = () => (
  <section className="method shell" id="method">
    <div className="sectionIntro dark"><p className="eyebrow">HOW IT WORKS</p><h2>From your resume to a clearer professional story.</h2>
      <p>Every step clarifies what is already true—not manufacture a story you cannot defend.</p></div>
    <div className="methodShowcase">
      <div className="steps">
        <Step n="1" t="Upload your resume" s="Use a PDF, DOCX, TXT file, or paste the text directly." />
        <Step n="2" t="Get your free analysis" s="See your score, evidence gaps, and prioritized recommendations." />
        <Step n="3" t="Choose your next step" s="Improve it yourself, develop a new resume, or build an AI career roadmap." />
        <Step n="4" t="Receive and verify" s="Download an editable result and confirm every claim before use." />
      </div>
      <aside className="founderStory">
        <div className="founderStoryImage">
          <img src="/aor-assets/hero.webp" alt="ArtOfResume method presentation" loading="lazy" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          <a href="#founder" aria-label="Read the ArtOfResume method" data-testid="site-method-play">▶</a>
        </div>
        <div><b>A message behind the method</b>
          <p>“Your experience has more value than a generic template can show. The job is to make that value clear without inventing anything.”</p>
          <small>— ArtOfResume method</small></div>
      </aside>
    </div>
  </section>
);

const AUD = [["QA", "QA and SDET", "Show automation depth, quality ownership, risk reduction, and release impact."],
  ["AU", "Automation engineers", "Connect frameworks and tools to maintainability, coverage, and delivery outcomes."],
  ["SE", "Software engineers", "Surface system scope, decisions, reliability, scale, and business contribution."],
  ["CL", "Cloud and platform", "Translate architecture, operations, security, and resilience into evidence."],
  ["LD", "Leads and managers", "Prove team influence, execution, stakeholder decisions, and measurable change."],
  ["AI", "Professionals moving into AI", "Turn transferable experience into a realistic, evidence-building roadmap."]];

const Heading = ({ e, h, p }) => <div className="landingHeading"><div><p className="eyebrow">{e}</p><h2>{h}</h2></div>{p && <p>{p}</p>}</div>;

export const Audience = () => (
  <section className="audienceSection shell">
    <Heading e="BUILT FOR EXPERIENCED PROFESSIONALS" h="Your title can change. Your evidence travels with you." p="The system is useful when your real work is stronger than the way your current resume presents it." />
    <div className="audienceGrid">{AUD.map(([s, t, p]) => <article key={s}><span>{s}</span><h3>{t}</h3><p>{p}</p></article>)}</div>
  </section>
);

const PROOF = [["10", "Observable resume signals", "Each finding states what was detected, why it matters, and the most useful truthful fix."],
  ["5", "Career diagnostic questions", "A short assessment covering your area, goal, experience, blockers and available hours."],
  ["7", "Personalised guide pages", "A PDF with your starting route, a suggested first project, and next actions."],
  ["3", "Editable resume formats", "Review on screen, then download PDF, DOCX, or plain text and verify every claim."]];

export const Proof = () => (
  <section className="proofSection">
    <div className="shell">
      <Heading e="VALUE YOU CAN VERIFY" h="No anonymous praise. Inspect the product itself." p="Credibility comes from transparent methods, editable outputs, and controls the user can see." />
      <div className="proofGrid">{PROOF.map(([b, h, p]) => <article key={h}><b>{b}</b><h3>{h}</h3><p>{p}</p></article>)}</div>
    </div>
  </section>
);

export const Outcomes = () => (
  <section className="outcomes shell">
    <div><strong>10</strong><span>signals assessed</span></div>
    <div><strong>0</strong><span>resumes stored by the free check</span></div>
    <div><strong>3</strong><span>fixes prioritized</span></div>
    <p>The diagnostic describes observable document signals. It does not guarantee interviews or employment.</p>
  </section>
);

export const Founder = () => (
  <section className="founder shell" id="founder">
    <div className="founderMark">AR</div>
    <div><p className="eyebrow">A MESSAGE FROM THE FOUNDER</p><h2>Your experience has more value than a generic template can show.</h2>
      <p>Art of Resume preserves the person’s truth, sharpens the evidence, and makes professional value legible to a hiring panel.</p></div>
  </section>
);

export const Pilot = () => (
  <section className="pilot" id="pilot">
    <div className="shell pilotInner">
      <div><p className="eyebrow">AI PROJECT PROOF COHORT</p><h2>Start with a realistic first project milestone.</h2>
        <p>Built from LLM Foundations, Prompt Engineering, and Agentic AI teaching, with 9+ years in quality engineering and 1,000+ APIs worked on.</p></div>
      <div className="contactBox"><p>Project fit call</p>
        <a href="https://calendly.com/rakesh-bharatreborn/30min" target="_blank" rel="noreferrer" onClick={() => trackConversion("book_call")} data-testid="site-book-call">Book a 30-minute call →</a>
        <small>We will align the scope, evidence, and next step.</small>
        <a href={funnelHref("/#pathway")} data-testid="site-see-courses" style={{ marginTop: 10 }}>See the learning pathway →</a></div>
    </div>
  </section>
);

const FAQ = [["Is the resume diagnostic free?", "Yes. The browser-based ten-signal analysis is free and does not require an account."],
  ["Will the diagnostic store my resume?", "No. The free analysis reads and evaluates the document in your browser. Resume Studio clearly asks for consent before information is sent or stored."],
  ["Will AI invent achievements?", "It should not. Resume development is instructed to preserve facts, and you must verify every date, metric, technology, responsibility, and outcome before use."],
  ["Which file formats are supported?", "The diagnostic and resume studio support TXT, DOCX, and text-based PDF documents up to 5 MB."],
  ["Is the output ATS-friendly?", "The generated resume uses conventional headings and a text-based layout. No tool can guarantee the behavior of every employer's ATS."],
  ["What does the AI career diagnostic provide?", "The 5-minute diagnostic suggests a starting route and course, and emails you a personalised PDF guide with a first project and next steps."]];

export const Faq = () => (
  <section className="faqSection shell">
    <Heading e="FREQUENTLY ASKED QUESTIONS" h="Clear answers before you upload." />
    <div className="faqGrid">{FAQ.map(([q, a], i) => <details key={q} data-testid={`site-faq-${i}`}><summary>{q}</summary><p>{a}</p></details>)}</div>
  </section>
);

export const SiteFooter = () => (
  <footer>
    <div className="shell footerInner">
      <a className="brand light" href="#top"><span className="brandWords">Art of Resume<small>HR will get back ... this time</small></span></a>
      <p>HR will get back ... this time</p>
      <div><a href="#lab">Analyze my resume</a><a href="/blog" data-testid="site-footer-blog">Blog</a><a href="/privacy" data-testid="site-footer-privacy">Privacy</a><a href="mailto:rakesh@bharatreborn.com">Contact</a></div>
    </div>
    <div className="shell legal" data-testid="site-footer-legal">© {new Date().getFullYear()} Bharat Reborn Private Limited. {LEGAL_LINE} Text entered in Analyze my resume stays in your browser; information submitted through Resume Studio is stored for service delivery under the privacy notice.</div>
  </footer>
);
